import { useEffect, useMemo, useState } from 'react';
import Lightbox, { type Slide } from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { dateFormat } from '../../domain/trail';
import type { Photo } from '../../schemas';
import { EmptyAlbum, LightboxCounter, LightboxMeta, PhotoGallery, PhotoGalleryThumb } from '../../styles';

const navigationDurationMs = 260;

export function DayPhotos({
  dayDate,
  photos,
  journeyPhotos,
  spotlightPhotoId,
  onActiveDayChange,
  onPhotoHighlightChange,
  onSpotlightClose,
}: {
  dayDate: string;
  photos: Photo[];
  journeyPhotos: Photo[];
  spotlightPhotoId: string | null;
  onActiveDayChange: (date: string) => void;
  onPhotoHighlightChange: (photo: Photo | null) => void;
  onSpotlightClose: () => void;
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activePhoto = activeIndex === null ? null : journeyPhotos[activeIndex];
  const journeyIndexById = useMemo(
    () => new Map(journeyPhotos.map((photo, index) => [photo.id, index] as const)),
    [journeyPhotos],
  );
  const slides = useMemo<Slide[]>(
    () =>
      journeyPhotos.map((photo, index) => ({
        src: photo.spotlight_path,
        alt: `Te Araroa on ${photo.capture_date}, journey photo ${index + 1} of ${journeyPhotos.length}`,
        ...(photo.width && photo.height ? { width: photo.width, height: photo.height } : {}),
      })),
    [journeyPhotos],
  );
  const activeDayPosition = useMemo(() => {
    if (!activePhoto) return null;
    const activeDayPhotos = journeyPhotos.filter((photo) => photo.capture_date === activePhoto.capture_date);
    return {
      index: activeDayPhotos.findIndex((photo) => photo.id === activePhoto.id),
      count: activeDayPhotos.length,
    };
  }, [activePhoto, journeyPhotos]);

  useEffect(() => {
    if (!spotlightPhotoId) return;
    const index = journeyIndexById.get(spotlightPhotoId);
    if (index !== undefined) setActiveIndex(index);
  }, [journeyIndexById, spotlightPhotoId]);

  useEffect(() => {
    const nextDate = activePhoto?.capture_date;
    if (!nextDate || nextDate === dayDate) return;
    const timer = window.setTimeout(() => onActiveDayChange(nextDate), navigationDurationMs);
    return () => window.clearTimeout(timer);
  }, [activePhoto?.capture_date, dayDate, onActiveDayChange]);

  if (!photos.length) return <EmptyAlbum>No photos matched this day.</EmptyAlbum>;

  return (
    <>
      <PhotoGallery aria-label="Photos from this day" onPointerLeave={() => onPhotoHighlightChange(null)}>
        {photos.map((photo, index) => (
          <PhotoGalleryThumb
            key={photo.id}
            type="button"
            aria-label={`Open photo ${index + 1} of ${photos.length} full screen`}
            onClick={() => {
              onPhotoHighlightChange(null);
              const journeyIndex = journeyIndexById.get(photo.id);
              if (journeyIndex !== undefined) setActiveIndex(journeyIndex);
            }}
            onPointerEnter={() => onPhotoHighlightChange(photo)}
            onFocus={() => onPhotoHighlightChange(photo)}
            onBlur={() => onPhotoHighlightChange(null)}
          >
            <img src={photo.thumbnail_path} alt="" loading="lazy" decoding="async" />
          </PhotoGalleryThumb>
        ))}
      </PhotoGallery>

      <Lightbox
        open={activePhoto !== null}
        close={() => {
          setActiveIndex(null);
          onSpotlightClose();
        }}
        index={activeIndex ?? 0}
        slides={slides}
        on={{ view: ({ index }) => setActiveIndex(index) }}
        carousel={{
          finite: true,
          preload: Math.min(4, Math.max(1, journeyPhotos.length - 1)),
          padding: '4%',
          spacing: '12%',
          imageFit: 'contain',
        }}
        controller={{ closeOnBackdropClick: true, closeOnEscape: true }}
        animation={{
          fade: 220,
          swipe: 350,
          navigation: navigationDurationMs,
          easing: {
            fade: 'ease-out',
            swipe: 'cubic-bezier(0.16, 1, 0.3, 1)',
            navigation: 'cubic-bezier(0.16, 1, 0.3, 1)',
          },
        }}
        className="te-araroa-lightbox"
        labels={{ Next: 'Next photo', Previous: 'Previous photo', Close: 'Close photo viewer' }}
        render={{
          controls: () =>
            activePhoto && activeIndex !== null && activeDayPosition ? (
              <LightboxMeta>
                <span>{dateFormat.format(new Date(`${activePhoto.capture_date}T12:00:00+12:00`))}</span>
                <LightboxCounter>
                  {String(activeDayPosition.index + 1).padStart(2, '0')} /{' '}
                  {String(activeDayPosition.count).padStart(2, '0')}
                </LightboxCounter>
              </LightboxMeta>
            ) : null,
        }}
      />
    </>
  );
}
